import { type Theme, type ResolvedTheme } from '../store/theme';

/**
 * Theme configuration with display information
 */
export const THEME_CONFIG = {
  light: {
    label: 'Light',
    description: 'Light theme',
    icon: 'sun',
  },
  dark: {
    label: 'Dark', 
    description: 'Dark theme',
    icon: 'moon',
  },
  system: {
    label: 'System',
    description: 'Follow system preference',
    icon: 'monitor',
  },
} as const;

/**
 * Get theme display information
 */
export const getThemeConfig = (theme: Theme) => {
  return THEME_CONFIG[theme];
};

/**
 * Get all available themes
 */
export const getAllThemes = (): Theme[] => {
  return Object.keys(THEME_CONFIG) as Theme[];
};

/**
 * Check if a string is a valid theme
 */
export const isValidTheme = (value: string): value is Theme => {
  return getAllThemes().includes(value as Theme);
};

/**
 * Get the next theme in cycle (for simple toggle)
 */
export const getNextTheme = (currentTheme: Theme): Theme => {
  const themes = getAllThemes();
  const currentIndex = themes.indexOf(currentTheme);
  const nextIndex = (currentIndex + 1) % themes.length;
  return themes[nextIndex];
};

/**
 * Get theme for simple light/dark toggle (excludes system)
 */
export const getToggleTheme = (currentResolvedTheme: ResolvedTheme): ResolvedTheme => {
  return currentResolvedTheme === 'light' ? 'dark' : 'light';
};

/**
 * Format theme name for display
 */
export const formatThemeName = (theme: Theme): string => {
  return THEME_CONFIG[theme].label;
};

/**
 * Get theme icon name
 */
export const getThemeIcon = (theme: Theme): string => {
  return THEME_CONFIG[theme].icon;
};

/**
 * Get resolved theme icon (for when system theme is resolved)
 */
export const getResolvedThemeIcon = (resolvedTheme: ResolvedTheme): string => {
  return resolvedTheme === 'dark' ? 'moon' : 'sun';
}; 